import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppStateService, MetricsService, TRACKABLE_EVENTS } from '@fb-tool-front/core/services';
import { AuthService } from '@fb-tool-front/core/services/auth.service';
import { ErrorService } from '@fb-tool-front/core/services/error.service';
import { LocaleService } from '@fb-tool-front/core/services/locale.service';
import {
  PASSWORD_MAX_LENGTH,
  TRACKABLE_EVENTS_AUTH,
  USERNAME_MAX_LENGTH
} from '@fb-tool-front/shared/constants/auth.constants';
import { LoginPageTypes, User } from '@fb-tool-front/shared/interfaces/auth.type';
import { environment } from '@fb-tool-front/environments/environment';
import { LOCATION } from '@ng-web-apis/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TuiValidationError } from '@taiga-ui/cdk';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, filter, finalize, map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginPageComponent implements OnInit {
  env = environment;
  public currentFormName: LoginPageTypes = 'login';

  private backendError$$ = new Subject<string>();
  public backendError$ = this.backendError$$.asObservable().pipe(
    filter(value => !!value),
    switchMap(value => this.translateService.stream(value))
  );
  private isAuthenticating$$ = new BehaviorSubject<boolean>(false);
  public isAuthenticating$ = this.isAuthenticating$$.asObservable();
  private isAuthenticatingFacebook$$ = new BehaviorSubject<boolean>(false);
  public isAuthenticatingFacebook$ = this.isAuthenticatingFacebook$$.asObservable();
  private isCaptchaRequired$$ = new BehaviorSubject<boolean>(false);
  public isCaptchaRequired$ = this.isCaptchaRequired$$.asObservable();
  public isLangChangeShown$: Observable<boolean>;

  readonly errorUsernameRequired$ = this.translateService
    .stream('Email is required')
    .pipe(map(value => new TuiValidationError(value)));

  readonly errorUsernameEmail$ = this.translateService
    .stream('Email is incorrect')
    .pipe(map(value => new TuiValidationError(value)));

  readonly errorUsernameMaxLength$ = this.translateService
    .stream(`Username can't be more than USERNAME_MAX_LENGTH characters long`, {
      value: USERNAME_MAX_LENGTH
    })
    .pipe(map(value => new TuiValidationError(value)));

  readonly errorPasswordRequired$ = this.translateService
    .stream('Password is required')
    .pipe(map(value => new TuiValidationError(value)));

  readonly errorPasswordMaxLength$ = this.translateService
    .stream(`Password can't be more than PASSWORD_MAX_LENGTH characters long`, {
      value: PASSWORD_MAX_LENGTH
    })
    .pipe(map(value => new TuiValidationError(value)));

  readonly errorCaptchaRequired$ = this.translateService
    .stream('Captcha is required')
    .pipe(map(value => new TuiValidationError(value)));

  readonly autocompleteUsername = 'email';
  readonly autocompleteCurrentPassword = 'current-password';

  loginForm = this.formBuilder.group({
    username: [
      null,
      [Validators.required, Validators.maxLength(USERNAME_MAX_LENGTH)] // TODO add email validator
    ],
    password: [null, [Validators.required, Validators.maxLength(PASSWORD_MAX_LENGTH)]],
    captcha: [null]
  });

  resetForm = this.formBuilder.group({
    username: [
      null,
      [Validators.required, Validators.maxLength(USERNAME_MAX_LENGTH), Validators.email]
    ],
    captcha: [null, Validators.required]
  });

  public getError(errorMessage: string) {
    return new TuiValidationError(errorMessage);
  }

  constructor(
    public authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private metricsService: MetricsService,
    private errorSerivce: ErrorService,
    public appStateService: AppStateService,
    public localeService: LocaleService,
    public translateService: TranslateService,
    @Inject(LOCATION) readonly location: Location
  ) {}

  ngOnInit(): void {
    this.isLangChangeShown$ = this.authService.user$.pipe(
      map((user: User) => user?.roles?.includes('ROLE_CHANGE_LANG'))
    );
    this.metricsService.trackEvent(TRACKABLE_EVENTS_AUTH.visitLogin());
  }

  public changeTab(value: LoginPageTypes) {
    this.currentFormName = value;
    this.loginForm.reset();
    this.resetForm.reset();
    this.backendError$$.next(null);
  }

  public reset() {
    this.resetForm.markAllAsTouched();

    if (!this.resetForm.valid) {
      return;
    }

    this.backendError$$.next(null);
    this.isAuthenticating$$.next(true);

    this.authService
      .resetPassword(this.resetForm.get('username').value, this.resetForm.get('captcha').value)
      .pipe(
        map(res => true),
        catchError(err => {
          this.errorSerivce.captureError(err);

          this.backendError$$.next(
            err?.error?.error?.message || err?.error?.message || 'Something went wrong'
          );

          this.resetForm.get('captcha').reset();
          this.resetForm.get('captcha').markAsUntouched();

          return of(null);
        }),
        filter(res => !!res),
        finalize(() => this.isAuthenticating$$.next(false)),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.changeTab('confirm');
      });
  }

  public loginWithFacebook() {
    this.metricsService.trackEvent(TRACKABLE_EVENTS.loginWithFacebook());
    this.isAuthenticatingFacebook$$.next(true);

    this.authService
      .loginWithFacebook()
      .pipe(
        catchError(error => {
          this.errorSerivce.captureError(error);
          this.isAuthenticatingFacebook$$.next(false);

          return of(null);
        }),
        filter(res => !!res),
        untilDestroyed(this)
      )
      .subscribe(res => {
        if (res.url) {
          this.localeService.saveLocaleToLocalStorage();
          this.location.href = res.url;
        }
      });
  }

  public login() {
    this.loginForm.markAllAsTouched();

    if (!this.loginForm.valid) {
      return;
    }

    this.backendError$$.next(null);
    this.isAuthenticating$$.next(true);

    this.authService
      .login(
        this.loginForm.get('username').value,
        this.loginForm.get('password').value,
        this.loginForm.get('captcha').value
      )
      .pipe(
        catchError(err => {
          this.errorSerivce.captureError(err);

          if (err?.error?.error?.type === 'UserUnconfirmedException') {
            this.authService.temporaryUser = this.loginForm.get('username').value;
            this.router.navigate(['/confirm']);
          } else if (err?.error?.error?.type === 'InvalidCaptchaException') {
            this.isCaptchaRequired$$.next(true);
            this.loginForm.get('captcha').setValidators(Validators.required);
          } else {
            this.backendError$$.next(
              err?.status === 401
                ? 'Username or password is incorrect'
                : err?.error?.error?.message || err?.error?.message || 'Something went wrong'
            );
          }

          this.loginForm.get('captcha').reset();
          this.loginForm.get('captcha').markAsUntouched();

          return of(null);
        }),
        filter(res => !!res),
        finalize(() => this.isAuthenticating$$.next(false)),
        untilDestroyed(this)
      )
      .subscribe(_ => {
        this.router.navigate(['']);
      });
  }
}
